import React from 'react';
import { Phone, Mail, MapPin, Facebook, Instagram, Twitter } from 'lucide-react';
import { contactInfo, doctorInfo } from '../data/mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const TikTokIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-.88-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
    </svg>
  );

  const footerLinks = {
    services: [
      { name: 'Dermatología Médica', href: '#services' },
      { name: 'Medicina Estética', href: '#services' },
      { name: 'Cirugía Dermatológica', href: '#services' },
      { name: 'Oncología Cutánea', href: '#services' }
    ],
    quickLinks: [
      { name: 'Acerca del Dr. Cuéllar', href: '#about' },
      { name: 'Galería de Tratamientos', href: '#gallery' },
      { name: 'Testimonios', href: '/testimonios' },
      { name: 'Ubicación', href: '#contact' }
    ],
    resources: [
      { name: 'Consejos de Cuidado', href: '#tips' },
      { name: 'Reseñas de Pacientes', href: '/testimonios' },
      { name: 'Horarios de Atención', href: '#contact' },
      { name: 'Contacto', href: '#contact' }
    ]
  };

  const socialLinks = [
    { icon: Instagram, label: 'Instagram', href: 'https://www.instagram.com/adrian.cuellar.derma/' },
    { icon: TikTokIcon, label: 'TikTok', href: 'https://www.tiktok.com/@adrian.cuellar.derma' }
  ];

  const whatsappUrl = `https://wa.me/${contactInfo.whatsapp.replace(/\D/g, '')}?text=${encodeURIComponent(contactInfo.whatsappMessage)}`;

  return (
    <footer className="bg-primary text-white pt-16 pb-8">
      <div className="container">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Practice Info */}
          <div className="lg:col-span-1">
            <h3 className="text-xl font-semibold text-white mb-4">Dr. Adrián Cuéllar Barboza</h3>
            <p className="text-sm text-gray-200 mb-6">
              Atención dermatológica especializada combinando tratamientos médicos con soluciones estéticas para una piel saludable y hermosa.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-3">
              <a href={whatsappUrl} className="flex items-center gap-3 hover:text-accent-purple-400 transition-colors">
                <Phone size={16} className="text-gray-300" />
                <span className="text-sm text-white">{contactInfo.phone}</span>
              </a>
              <div className="flex items-center gap-3">
                <Mail size={16} className="text-gray-300" />
                <span className="text-sm text-white">{contactInfo.email}</span>
              </div>
              <a href={contactInfo.googleMapsUrl} className="flex items-start gap-3 hover:text-accent-purple-400 transition-colors text-left">
                <MapPin size={16} className="text-gray-300 mt-1 flex-shrink-0" />
                <div className="text-sm text-white">
                  {contactInfo.address.street}<br />
                  {contactInfo.address.reference}<br />
                  {contactInfo.address.neighborhood}<br />
                  {contactInfo.address.city}, {contactInfo.address.state} {contactInfo.address.zip}
                </div>
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-4">Servicios</h4>
            <ul className="space-y-2">
              {footerLinks.services.map((service, index) => (
                <li key={index}>
                  <a 
                    href={service.href} 
                    className="text-sm text-gray-200 hover:text-white transition-colors"
                    onClick={(e) => {
                      if (service.href.startsWith('#')) {
                        e.preventDefault();
                        const element = document.querySelector(service.href);
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth' });
                        }
                      }
                    }}
                  >
                    {service.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-4">Enlaces Rápidos</h4>
            <ul className="space-y-2">
              {footerLinks.quickLinks.map((link, index) => (
                <li key={index}>
                  <a 
                    href={link.href} 
                    className="text-sm text-gray-200 hover:text-white transition-colors"
                    onClick={(e) => {
                      if (link.href.startsWith('#')) {
                        e.preventDefault();
                        const element = document.querySelector(link.href);
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth' });
                        }
                      }
                    }}
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-4">Recursos</h4>
            <ul className="space-y-2">
              {footerLinks.resources.map((resource, index) => (
                <li key={index}>
                  <a 
                    href={resource.href} 
                    className="text-sm text-gray-200 hover:text-white transition-colors"
                    onClick={(e) => {
                      if (resource.href.startsWith('#')) {
                        e.preventDefault();
                        const element = document.querySelector(resource.href);
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth' });
                        }
                      }
                    }}
                  >
                    {resource.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Social Media */}
        <div className="border-t border-white/20 pt-8 mb-8">
          <div className="flex items-center justify-center gap-4">
            <span className="text-sm text-gray-200">Síguenos:</span>
            <div className="flex gap-3">
              {socialLinks.map((social, index) => {
                const IconComponent = social.icon;
                return (
                  <a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors text-white"
                  >
                    <IconComponent size={16} />
                  </a>
                );
              })}
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/20 pt-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-center md:text-left">
            <div className="text-sm text-gray-200">
              © {currentYear} Dr. Adrián Cuéllar Barboza. Todos los derechos reservados.
            </div>
            <div className="flex gap-6">
              <a href="#" className="text-sm text-gray-200 hover:text-white transition-colors">
                Política de Privacidad
              </a>
              <a href="#" className="text-sm text-gray-200 hover:text-white transition-colors">
                Términos de Servicio
              </a>
              <a href="#" className="text-sm text-gray-200 hover:text-white transition-colors">
                Aviso HIPAA
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;